import React, { useEffect, useRef, useState } from 'react';
import { VideoOff } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Button, Space, Card, Row, Col } from 'antd';
import { AudioMutedOutlined, AudioOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import lobbyIllustration from '../../assets/images/waitingLobby.png';
import { fetchMeeting, setCurrentInterviewPage } from '../../store/actions/MeetingActions';
import * as streamActions from '../../store/actions/InterviewRecordingActions';
import { initAudioStream, stopAudioStream } from '../../store/actions/InterviewRecordingActions';
import * as actions from '../../store/actions';

import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
} from 'amazon-chime-sdk-js';

import styles from './WaitingRoom.scss';

const { Title, Text } = Typography;

export default function WaitingRoom() {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();

  const { meeting } = useSelector(state => state.meeting);
  const { videoStream, selectedVideoInput, selectedAudioInput, hasVideo, hasAudio, screenStream } =
    useSelector(state => state.interviewRecording);
  const { participants } = useSelector(state => state.interview);

  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [cameraOn, setCameraOn] = useState(false);

  const urlparams = new URLSearchParams(window.location.search);
  const interview_id = urlparams.get('interview_id');

  const createMeeting = async meeting => {
    if (!meeting || !participants) return;

    const email_id = localStorage.getItem('username');
    const participant = participants.find(p => p.email === email_id);
    if (!participant) return;

    try {
      const attendeeData = await dispatch(actions.fetchAttendee(meeting.id, participant.id));

      const configuration = new MeetingSessionConfiguration(
        {
          MeetingId: meeting.aws_meeting_id,
          MediaPlacement: meeting.aws_media_placement,
          ExternalMeetingId: meeting.id,
        },
        {
          AttendeeId: attendeeData.aws_attendee_id,
          JoinToken: attendeeData.aws_join_token,
        },
      );

      const logger = new ConsoleLogger('WaitingRoomLogger', LogLevel.INFO);
      const deviceController = new DefaultDeviceController(logger);

      const meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);

      if (selectedAudioInput) {
        await meetingSession.audioVideo.startAudioInput(selectedAudioInput);
      }

      if (selectedVideoInput) {
        await meetingSession.audioVideo.startVideoInput(selectedVideoInput);
      }

      dispatch(streamActions.startMeetingSession(meetingSession));

      setTimeout(async () => {
        if (screenStream) {
          await meetingSession.audioVideo.startContentShare(screenStream);
        }
      }, 3000);

      dispatch(setCurrentInterviewPage('meeting'));
    } catch (error) {
      console.error('Error creating meeting session:', error);
    }
  };

  useEffect(() => {
    if (!interview_id || meeting?.id) return;

    const interval = setInterval(async () => {
      const meetingData = await dispatch(fetchMeeting(interview_id));
      if (meetingData) {
        createMeeting(meetingData);
        clearInterval(interval);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [dispatch, interview_id]);

  useEffect(() => {
    if (videoRef.current && videoStream) {
      videoRef.current.srcObject = videoStream;
    }
  }, [videoStream]);

  useEffect(() => setCameraOn(hasVideo), [hasVideo]);
  useEffect(() => setMuted(!hasAudio), [hasAudio]);

  const toggleAudioStream = () => {
    muted ? dispatch(initAudioStream()) : dispatch(stopAudioStream());
  };

  const toggleVideoStream = () => {
    cameraOn
      ? dispatch(streamActions.stopVideoStream())
      : dispatch(streamActions.initVideoStream());
  };

  return (
    <div className={styles['waiting-room-container']}>
      <Card className={styles['waiting-room-card']}>
        <div className={styles['waiting-room-header']}>
          <Title
            level={3}
            className={styles['waiting-room-title']}
          >
            {t('waiting_room.title')}
          </Title>

          <Text className={styles['waiting-room-subtitle']}>{t('waiting_room.subtitle')}</Text>
        </div>

        <div className={styles['waiting-room-content']}>
          <Row gutter={32}>
            <Col
              span={12}
              className={styles['waiting-room-left-column']}
            >
              <img
                src={lobbyIllustration}
                alt={t('waiting_room.lobby_image_alt')}
                className={styles['waiting-room-image']}
              />
            </Col>

            <Col
              span={12}
              className={styles['waiting-room-right-column']}
            >
              <div className={styles['waiting-room-section']}>
                <div className={styles['waiting-room-icon-section']}>
                  <VideoCameraOutlined style={{ marginRight: 8, color: '#666' }} />
                  <Text strong>{t('waiting_room.camera_preview_title')}</Text>
                </div>

                <div className={styles['waiting-room-video-container']}>
                  <video
                    ref={videoRef}
                    className={styles['waiting-room-video']}
                    autoPlay
                    muted
                    playsInline
                  />

                  {!hasVideo && (
                    <div className={styles['waiting-room-video-overlay']}>
                      <VideoCameraOutlined style={{ fontSize: 32, marginBottom: 8 }} />
                      <div>{t('waiting_room.camera_preview_placeholder')}</div>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Space size="middle">
                      <Button
                        shape="circle"
                        onClick={toggleAudioStream}
                        icon={muted ? <AudioMutedOutlined /> : <AudioOutlined />}
                        aria-label={t('waiting_room.toggle_microphone')}
                      />
                      <Button
                        shape="circle"
                        onClick={toggleVideoStream}
                        icon={cameraOn ? <VideoCameraOutlined /> : <VideoOff size={17} />}
                        aria-label={t('waiting_room.toggle_camera')}
                      />
                    </Space>
                  </div>

                  <Text
                    type="secondary"
                    style={{ fontSize: '14px' }}
                  >
                    {t('waiting_room.footer_note')}
                  </Text>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );
}
